import useQuery from "utils/hooks/useQuery.ts";
import {
  INIT_DATA_QUERY_PARAM,
  LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY,
  QUICK_CONNECT_DATA_QUERY_PARAM,
  TG_START_QUERY_PARAM,
} from "common/constants.ts";
import {
  useRawInitData,
  on,
  viewportSafeAreaInsets,
  viewportContentSafeAreaInsets,
  viewport,
  swipeBehavior,
  requestSafeAreaInsets,
  requestContentSafeAreaInsets,
} from "@telegram-apps/sdk-react";
import { Inset, TelegramWebappQueryData } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  displayResultMessage,
  redirectWithTelegramWebData,
  showIntercom,
} from "utils/misc.tsx";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { AnalyticsContext } from "analytics/AnalyticsProvider.tsx";
import { useHistory, useLocation } from "react-router-dom";
import { captureTelegramError } from "utils/sentry.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants.ts";
import {
  safeRetrieveLaunchParams,
  parseInitDataHash,
  getCleanOldInitDataHashes,
  markChannelEntry,
  saveSavedInitDataHashes,
} from "features/telegram/utils.ts";
import { useTelegramWebAppData } from "@shared/utils/hooks/misc.ts";

export const useTelegramInitData = () => {
  const query = useQuery();

  let urlInitData = "";
  try {
    urlInitData = atob(query.get(INIT_DATA_QUERY_PARAM) || "");
  } catch {}

  let telegramInitData = "";
  try {
    telegramInitData = useRawInitData() || "";
  } catch {}

  const initData = telegramInitData || urlInitData;

  return {
    initData,
    urlInitData,
    telegramInitData,
  };
};

export const useTelegramWebRedirect = () => {
  const location = useLocation();
  const { initData: defaultInitData } = useTelegramWebAppData();
  const [getWebAppInitData, getWebAppInitDataStatus] =
    sharedLib.sharedApi.authentication.useLazyGetWebAppInitDataQuery();

  /** Resolves with whether the website was opened. */
  const redirectToWebsite = async (
    additionalParams?: Record<string, string>
  ): Promise<boolean> => {
    return getWebAppInitData()
      .unwrap()
      .then((initData) => {
        const _initData = initData ?? defaultInitData;
        return (
          !!_initData &&
          redirectWithTelegramWebData(
            _initData,
            additionalParams,
            location.pathname
          )
        );
      });
  };

  return {
    redirectToWebsite,
    isLoading: getWebAppInitDataStatus.isLoading,
  };
};

export const useValidateTelegramWebData = (
  initData?: string,
  urlInitData?: string,
  telegramStartValue?: TelegramWebappQueryData
) => {
  const [validateWebAppInitData, validateInitDataStatus] =
    sharedLib.sharedApi.authentication.useValidateWebAppInitDataMutation();
  const { appendQuery } = useHistoryWithQuery();

  const validateInitData = async () => {
    if (!initData) {
      return Promise.reject();
    }

    const currentHash = parseInitDataHash(initData);
    const savedHashes = getCleanOldInitDataHashes();
    const isNew = currentHash ? !(currentHash in savedHashes) : false;

    if (currentHash && !isNew) {
      return Promise.reject();
    }
    return displayResultMessage(
      validateWebAppInitData({
        init_data: initData,
        ...telegramStartValue?.parameters,
      }).unwrap()
    )
      .catch(() => {})
      .finally(() => {
        if (urlInitData) {
          appendQuery(undefined, [INIT_DATA_QUERY_PARAM]);
        }
        if (currentHash) {
          savedHashes[currentHash] = Date.now();
          saveSavedInitDataHashes(savedHashes);
        }
      });
  };

  return {
    validateInitData,
    validateInitDataStatus,
  };
};

export const useTelegramStartupQuery = () => {
  const query = useQuery();

  const telegramStartValue = query.get(TG_START_QUERY_PARAM);
  return useMemo(() => {
    if (telegramStartValue) {
      try {
        return JSON.parse(atob(telegramStartValue.replaceAll("_", "=")));
      } catch (e) {}
    }
  }, [telegramStartValue]) as TelegramWebappQueryData | undefined;
};

const useDecodedUrlParam = (paramKey: string, parseJson?: boolean) => {
  const query = useQuery();
  const promoCode = query.get(paramKey);
  if (promoCode) {
    try {
      const parsedValue = atob(promoCode || "");
      if (parseJson) {
        return JSON.parse(parsedValue);
      }
      return parsedValue;
    } catch {}
  }
};

export const useTelegramWebAppProvider = () => {
  const history = useHistory();
  const { setExtraLoginParams, setIsInitialized: setIsAnalyticsInitialized } =
    useContext(AnalyticsContext);
  const { initData, urlInitData, telegramInitData } = useTelegramInitData();
  const [isInitialized, setIsInitialized] = useState(!initData);
  const telegramStartValue = useTelegramStartupQuery();
  const { validateInitData, validateInitDataStatus } =
    useValidateTelegramWebData(initData, urlInitData, telegramStartValue);
  const [getAuthUrl] =
    sharedLib.sharedApi.configuration.useLazyGetAuthUrlQuery();
  const quickConnectData = useDecodedUrlParam(
    QUICK_CONNECT_DATA_QUERY_PARAM,
    true
  );
  const isNewUserRef = useRef<boolean | undefined>(undefined);

  const handleSupportParam = () => {
    if (telegramStartValue?.parameters?.source === "support_bot") {
      showIntercom();
    }
  };

  const redirectToStartLocation = () => {
    if (telegramStartValue?.path) {
      // A channel post's button keeps the channel flow even for a user who
      // is still onboarding (the server keeps them in it).
      if (telegramStartValue.parameters?.source === "channel")
        markChannelEntry(telegramStartValue.path);
      history.push(telegramStartValue.path);
    }
  };

  useEffect(() => {
    if (isInitialized) {
      handleSupportParam();
      redirectToStartLocation();
    }
  }, [isInitialized]);

  useEffect(() => {
    if (!IS_TELEGRAM_WEB_APP) return;

    const launchParams = safeRetrieveLaunchParams();
    try {
      if (swipeBehavior.disableVertical.isAvailable()) {
        swipeBehavior.disableVertical();
      }
    } catch (error) {
      captureTelegramError(error, {
        method: "swipeBehavior.disableVertical",
        location: "useTelegramWebAppData - initial setup",
        additionalData: { launchParams },
      });
    }
    setTimeout(function expandTelegramApp() {
      try {
        if (viewport.expand.isAvailable()) {
          viewport.expand();
        }
      } catch (error) {
        captureTelegramError(error, {
          method: "viewport.expand",
          location: "useTelegramWebAppData - viewport expansion (setTimeout)",
          additionalData: { launchParams },
        });
      }
      try {
        if (viewport.requestFullscreen.isAvailable()) {
          viewport.requestFullscreen().catch((error) => {
            captureTelegramError(error, {
              method: "viewport.requestFullscreen",
              location:
                "useTelegramWebAppData - fullscreen request (setTimeout)",
              additionalData: { launchParams },
            });
          });
        }
      } catch (error) {
        captureTelegramError(error, {
          method: "viewport.requestFullscreen",
          location:
            "useTelegramWebAppData - fullscreen availability check (setTimeout)",
          additionalData: { launchParams },
        });
      }
    }, 500);
  }, []);

  useEffect(() => {
    if (validateInitDataStatus.isError && !isInitialized) {
      setIsInitialized(true);
    }
  }, [validateInitDataStatus.isError]);

  useEffect(() => {
    if (isInitialized) return;
    validateInitData()
      ?.then((response) => {
        if (quickConnectData) {
          getAuthUrl(quickConnectData)
            .unwrap()
            .then((response) => {
              localStorage?.setItem(
                LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY,
                performance.now().toString()
              );
              window.open(response, "_self");
            })
            .catch(() => {});
        } else {
          isNewUserRef.current = response?.data.is_new_user;
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!quickConnectData) {
          setIsInitialized(true);
          setExtraLoginParams?.({
            ...telegramStartValue?.parameters,
            is_new_user: isNewUserRef.current,
          } as any);
          setIsAnalyticsInitialized?.(true);
        }
      });
  }, [initData]);

  return {
    isInitialized,
    validateInitDataStatus,
    telegramStartValue,
    initData,
    urlInitData,
    telegramInitData,
  };
};

export const useTelegramSafeAreaInsets = () => {
  const defaultValue = { top: 0, right: 0, bottom: 0, left: 0 };

  const [safeAreaInset, setSafeAreaInset] = useState<Inset>(
    () => viewportSafeAreaInsets() || defaultValue
  );
  const [contentSafeAreaInset, setContentSafeAreaInset] = useState<Inset>(
    () => viewportContentSafeAreaInsets() || defaultValue
  );

  useEffect(() => {
    if (!IS_TELEGRAM_WEB_APP) return;

    let removeSafeAreaChangedListener: VoidFunction | undefined;
    let removeContentSafeAreaChangedListener: VoidFunction | undefined;

    try {
      removeSafeAreaChangedListener = on("safe_area_changed", (payload) => {
        setSafeAreaInset(payload);
      });
    } catch (error) {
      captureTelegramError(error, {
        method: "on safe_area_changed",
        location: "useTelegramSafeAreaInsets",
      });
    }

    try {
      requestSafeAreaInsets()?.catch((error) => {
        captureTelegramError(error, {
          method: "requestSafeAreaInsets - promise rejection",
          location: "useTelegramSafeAreaInsets",
        });
      });
    } catch {}

    try {
      removeContentSafeAreaChangedListener = on(
        "content_safe_area_changed",
        (payload) => {
          setContentSafeAreaInset(payload);
        }
      );
    } catch (error) {
      captureTelegramError(error, {
        method: "on content_safe_area_changed",
        location: "useTelegramSafeAreaInsets",
      });
    }

    try {
      requestContentSafeAreaInsets()?.catch((error) => {
        captureTelegramError(error, {
          method: "requestContentSafeAreaInsets - promise rejection",
          location: "useTelegramSafeAreaInsets",
        });
      });
    } catch {}

    return () => {
      removeSafeAreaChangedListener?.();
      removeContentSafeAreaChangedListener?.();
    };
  }, []);

  return {
    safeAreaInset,
    contentSafeAreaInset,
  };
};

export const useTelegramWebAppBottomPadding = () => {
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  return (safeAreaInset.bottom ?? 0) + (contentSafeAreaInset.bottom ?? 0);
};
